import { displayNames, collator } from './intl';

// Country names in the active language, from CLDR via Intl.DisplayNames.
//
// Deliberately NOT a translated string table and NOT a backend field:
// src/helpers/staticCountries.json already carries the ISO-3166 alpha-2 code for
// every row, and CLDR is both better maintained than anything we would ship
// and free at runtime. This is why country names are the one piece of
// "server-supplied content" that does not wait for the backend.
//
// The `?? iso2` fallback is load-bearing: a handful of codes in the CBAM
// dataset are not ISO-3166 regions (Kosovo's provisional XK, historical
// entries), and DisplayNames.of() throws RangeError on a malformed code rather
// than returning undefined.
export const countryName = (iso2, localeCode) => {
  if (!iso2 || typeof iso2 !== 'string') return '';
  const code = iso2.trim().toUpperCase();
  if (code.length !== 2) return iso2;
  try {
    return displayNames(localeCode, { type: 'region' }).of(code) ?? iso2;
  } catch {
    return iso2;
  }
};

// Resolve a country record from staticCountries.json (or an API payload) to a
// display name, preferring the localized name and falling back to whatever the
// record already carried.
export const countryDisplayName = (country, localeCode) => {
  if (!country) return '';
  if (typeof country === 'string') return countryName(country, localeCode) || country;
  const localized = country.iso2 ? countryName(country.iso2, localeCode) : '';
  // Only prefer the localized form when it actually resolved to something
  // other than the raw code, so a non-ISO entry keeps its English name.
  return localized && localized !== country.iso2 ? localized : (country.name ?? '');
};

// Sort by the ACTIVE language's collation, not by code unit.
//
// Plain .sort() puts "Åland" after "Zimbabwe" and ignores that Spanish files
// "Alemania" under A while Italian files "Germania" under G — so a dropdown
// sorted in English is in visibly random order once its labels are Spanish.
export const compareLocalized = (a, b, localeCode) =>
  collator(localeCode, { sensitivity: 'base', numeric: true }).compare(a ?? '', b ?? '');

// Sort a list of country records by their localized display name.
export const sortCountriesByName = (countries, localeCode) =>
  [...(countries ?? [])].sort((a, b) =>
    compareLocalized(
      countryDisplayName(a, localeCode),
      countryDisplayName(b, localeCode),
      localeCode,
    ),
  );

// Resolve a country reference — a populated `{_id, iso2, …}` record, a numeric
// id, or that same id as a STRING — against the `useAllCountries()` list.
//
// Compared as strings on purpose, same as `resolveCountryName`: `_id` is a
// number in staticCountries.json and in `/country/all`, but the identical
// field arrives as a string from route params, from the stock upload's match
// panels (`entity.countryId.toString()`), and from list endpoints that
// stringify ids. `find(c => c._id === ref)` silently yields nothing there —
// which is how a country cell loses its flag while still rendering a name, or
// renders the bare id with no popover at all.
export const findCountry = (countries, ref) => {
  if (ref == null || ref === '' || ref === -1 || ref === '-1') return null;
  if (typeof ref === 'object') {
    // Already-populated records carry everything the UI needs; only fall
    // through to the lookup when the payload is a bare `{_id}` stub.
    if (ref.iso2) return ref;
    return (countries ?? []).find((c) => String(c._id) === String(ref._id)) || ref;
  }
  return (countries ?? []).find((c) => String(c._id) === String(ref)) || null;
};

// Fold a string for search: lowercase, strip diacritics (NFD + combining
// marks), so "Türkiye", "turkiye" and "TÜRK" all compare equal.
export const foldSearch = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Names people type that neither CLDR nor our records carry, keyed by iso2.
// Deliberately tiny: add an entry only for a name users actually search by
// (colloquial, or a former name CLDR has renamed away from), not for spelling
// variants — diacritics are already folded and the CLDR English name is
// already searched. Former names matter because CLDR's English is the new
// name too (TR is "Türkiye" in English CLDR), and in a non-English UI the
// record's `name` is localized, so "Turkey" survives nowhere else. Two-letter
// aliases ("UK") match exactly, like the ISO code itself.
export const COUNTRY_ALIASES = {
  GB: ['UK', 'Great Britain', 'Britain', 'England'],
  US: ['USA', 'America', 'United States of America'],
  NL: ['Holland'],
  TR: ['Turkey'],
  CZ: ['Czech Republic'],
  CI: ['Ivory Coast'],
  SZ: ['Swaziland'],
};

// Everything a user might type for a country, folded into one haystack: the
// localized CLDR label, the CLDR English name, the record's `name` and
// `native` name, and any aliases. CLDR renames (TR → "Türkiye", CZ → "Czechia",
// CI → "Côte d'Ivoire") otherwise hide a country from anyone searching by the
// name they know. The English name is taken from CLDR rather than `name`
// because /country/all localizes `name` to the request language, so a German
// UI's records say "Türkei" and carry no English at all.
//
// The ISO code is NOT in here: as a substring it would make "us" match
// Russia and Australia. Codes are matched exactly via countrySearchCodes.
export const countrySearchText = (country, localeCode) =>
  foldSearch(
    [
      countryDisplayName(country, localeCode),
      country?.iso2 ? countryName(country.iso2, 'en') : '',
      country?.name,
      country?.native,
      ...(COUNTRY_ALIASES[country?.iso2] ?? []),
    ]
      .filter(Boolean)
      .join('\n'),
  );

// The short codes a query must equal exactly: the iso2 plus any alias.
export const countrySearchCodes = (country) =>
  [country?.iso2, ...(COUNTRY_ALIASES[country?.iso2] ?? [])].filter(Boolean).map(foldSearch);
